import React from 'react'
import AnonymousLayout from '@/layouts/Anonymous'
import SimpleLayout from '@/layouts/Simple'

import { LoginPage } from '@/components/auth/loginPage'
import { ForgotPasswordPage } from '@/components/auth/forgotPassword'
import { ResetPasswordPage } from '@/components/auth/resetPassword'
import { InstitutionSelectionPage } from '@/components/auth/institutionSelection'

import Home from './pages/Home'
import ReportSubmissionGroup from './pages/reporting/reportSubmissionGroup'
import ForecastReportSubmissionGroup from './pages/reporting/forecastReportSubmissionGroup'
import ReportSubmissionsPage from './pages/reporting/submissions/reportSubmissions'
import ReportSubmissionDetail from './pages/reporting/submissions/detail/reportSubmissionDetail'
import DocumentationViewer from './pages/documentation'
import { MessageDetail, MessageReply, Messages } from '@/components/messages'
import { UserManagementList, UserManagementAddUser, Detail as UserDetail } from './pages/usersManagement'
import DashboardSettings from '@/components/dashboard/settings/page'
import { EntityNotFound } from '@/components/EntityNotFound'
import { NotFound404 } from '@/components/404'
import { InstitutionDetail } from './pages/institution/detail'
import NotificationList from '@/components/notifications/list'
import UserProfile from '@/components/userProfile'
import { ChangeRequests } from './pages/changeRequests'
import { ChangeRequestDetail } from './pages/changeRequests/detail'
import { IndividualList } from './pages/individuals'
import { IndividualDetail } from './pages/individuals/detail'
import BackgroundJobList from '@/components/backgroundJobs/list'
import XmlSigningPage from './pages/reporting/submissions/xmlSigning'
import { InstitutionRedirection } from './institutionRedirection'
import { SubmitReport } from '@/report-app/pages/reporting/submissions/submit/submitReport'

const reportRoutes = [{
  exact: true,
  path: '/institution-redirection',
  component: InstitutionRedirection
}, {
  exact: true,
  path: '/login',
  component: LoginPage,
  layout: AnonymousLayout
}, {
  exact: true,
  path: '/login/forgot',
  component: ForgotPasswordPage,
  layout: AnonymousLayout
}, {
  exact: true,
  path: '/login/reset',
  component: ResetPasswordPage,
  layout: AnonymousLayout
}, {
  exact: true,
  path: '/login/institution',
  component: InstitutionSelectionPage,
  layout: SimpleLayout
}, {
  exact: true,
  path: '/demographics/institution-detail/~:id(\\d+)',
  component: InstitutionDetail,
  permission: 'CanViewInstitutions'
}, {
  exact: true,
  path: '/demographics/institution-detail',
  component: InstitutionDetail
}, {
  exact: true,
  path: '/demographics/individuals',
  component: IndividualList,
  permission: 'CanViewIndividuals'
}, {
  exact: true,
  path: '/demographics/individuals/:isChangeItemId(~)?:id(\\d+)?',
  component: IndividualDetail,
  permission: 'CanViewIndividuals'
}, {
  exact: true,
  path: '/demographics/change-requests',
  component: ChangeRequests,
  permission: 'CanViewChangeRequests'
}, {
  exact: true,
  path: '/demographics/change-requests/:id(\\d+)',
  component: ChangeRequestDetail,
  permission: 'CanViewChangeRequests'
}, {
  exact: true,
  path: '/report-submission-groups',
  component: ReportSubmissionGroup,
  permission: 'CanViewReports'
}, {
  exact: true,
  path: '/report-submission-groups/:parentId(\\d+)/report-submissions',
  component: ReportSubmissionsPage,
  permission: 'CanViewReports'
}, {
  exact: true,
  path: '/report-submission-groups/:parentId(\\d+)/report-submissions/xml-signing',
  component: XmlSigningPage,
  permission: 'CanViewReports'
}, {
  exact: true,
  path: '/report-submission-groups/:parentId(\\d+)/report-submissions/submit-report',
  component: SubmitReport,
  permission: 'CanViewReports'
}, {
  exact: true,
  path: '/report-submission-groups/:parentId(\\d+)/report-submissions/:id',
  component: ReportSubmissionDetail,
  permission: 'CanViewReports'
}, {
  exact: true,
  path: '/forecast-report-submission-groups',
  component: ForecastReportSubmissionGroup,
  permission: 'CanViewForecast'
}, {
  exact: true,
  path: '/forecast-report-submission-groups/:parentId(\\d+)/report-submissions',
  component: ReportSubmissionsPage,
  permission: 'CanViewForecast'
}, {
  exact: true,
  path: '/forecast-report-submission-groups/:parentId(\\d+)/report-submissions/:id',
  component: ReportSubmissionDetail,
  permission: 'CanViewForecast'
}, {
  exact: true,
  path: '/messages/:tab(\\w+)/new',
  render: props => <MessageDetail {...props} isNew isReport backUrl={'/messages/' + props.match.params.tab} />,
  permission: ['CanViewMessages', 'CanManageMessages', 'CanSendMessages']
}, {
  exact: true,
  path: '/messages/sent/:id(\\d+)',
  render: props => <MessageDetail {...props} isReport backUrl={'/messages/sent'} />,
  permission: ['CanViewMessages', 'CanManageMessages', 'CanSendMessages']
}, {
  exact: true,
  path: '/messages/:id(\\d+)',
  render: props => <MessageDetail {...props} isReport backUrl={'/messages/' + props.match.params.tab} />,
  permission: ['CanViewMessages', 'CanManageMessages', 'CanSendMessages']
}, {
  exact: true,
  path: '/messages/draft/:id(\\d+)',
  render: props => <MessageDetail isReport {...props} backUrl={'/messages/draft'} />,
  permission: ['CanViewMessages', 'CanManageMessages']
}, {
  exact: true,
  path: '/messages/:tab(\\w+)?',
  render: props => <Messages {...props} isReport />,
  permission: ['CanViewMessages', 'CanManageMessages', 'CanSendMessages']
}, {
  exact: true,
  path: '/messages/reply/:id(\\d+)/:replyId(\\d+)?',
  render: props => <MessageReply {...props} backUrl={'/messages/' + props.match.params.tab} />,
  permission: ['CanViewMessages', 'CanManageMessages', 'CanSendMessages']
}, {
  exact: true,
  path: '/users',
  component: UserManagementList,
  permission: 'CanManageUsers'
}, {
  exact: true,
  path: '/users/:id(\\d+)',
  component: UserDetail,
  permission: 'CanManageUsers'
}, {
  exact: true,
  path: '/users/add',
  component: UserManagementAddUser,
  permission: 'CanManageUsers'
}, {
  exact: true,
  path: '/documentation',
  component: DocumentationViewer,
  permission: 'CanViewDocumentation'
}, {
  exact: true,
  path: '/dashboard-settings',
  component: DashboardSettings,
  componentProps: { enumName: 'ReportAppDashboardWidgetType' }
}, {
  exact: true,
  path: '/notifications',
  component: NotificationList
}, {
  exact: true,
  path: '/background-jobs',
  component: BackgroundJobList
}, {
  exact: true,
  path: '/user-profile',
  component: UserProfile
}, {
  exact: true,
  path: '/',
  component: Home
}, {
  exact: true,
  path: '/not-found',
  component: EntityNotFound
}, {
  component: NotFound404
}]

function getReportRoutes () {
  return reportRoutes.map(x => { return { ...x } })
}

export { getReportRoutes }



// WEBPACK FOOTER //
// ./src/report-app/route-config.js